import { lazy, Suspense, useState, useMemo, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useProducts } from "@/hooks/ecommerce/useProducts";
import { toLegacyProductWithRelations } from "@/services/products.service";
import TopBar from "@/components/store/TopBar";
import StoreHeader from "@/components/store/StoreHeader";
import HeroBanner from "@/components/store/HeroBanner";
import { type StoreFilters, defaultFilters } from "@/components/store/StoreFilterSidebar";
import { applyStoreFilters } from "@/lib/storeFilters";
import ProductGrid from "@/components/store/ProductGrid";
import type { Product } from "@/data/products";

// Below-the-fold: split out of the initial bundle.
const StoreFilterSidebar = lazy(() => import("@/components/store/StoreFilterSidebar"));
const CategoryHighlights = lazy(() => import("@/components/store/CategoryHighlights"));
const InstitutionalBanner = lazy(() => import("@/components/store/InstitutionalBanner"));
const SocialProof = lazy(() => import("@/components/store/SocialProof"));
const Newsletter = lazy(() => import("@/components/store/Newsletter"));
const StoreFooter = lazy(() => import("@/components/store/StoreFooter"));

const Index = () => {
  const [filters, setFilters] = useState<StoreFilters>(defaultFilters);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const searchQuery = (searchParams.get("q") ?? "").trim().toLowerCase();

  const { data: dbProducts = [] } = useProducts({ activeOnly: true, withRelations: true });

  // Static fallback products are only used when the DB is empty.
  // Load them lazily so they don't bloat the initial bundle.
  const [staticProducts, setStaticProducts] = useState<Product[]>([]);
  useEffect(() => {
    if ((dbProducts as any[]).length === 0 && staticProducts.length === 0) {
      import("@/data/products").then((m) => setStaticProducts(m.products as Product[]));
    }
  }, [dbProducts, staticProducts.length]);

  useEffect(() => {
    const channel = supabase
      .channel("products-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "products" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["products"] });
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "categories" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["categories"] });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [queryClient]);

  const allProducts: Product[] = useMemo(() => {
    const mapped = (dbProducts as unknown as any[]).map(toLegacyProductWithRelations) as unknown as Product[];
    return mapped.length > 0 ? mapped : staticProducts;
  }, [dbProducts]);

  const filteredProducts = useMemo(() => {
    let result = applyStoreFilters(allProducts, dbProducts as any[], filters);
    if (searchQuery) {
      result = result.filter((p) => {
        const haystack = `${p.name ?? ""} ${p.category ?? ""}`.toLowerCase();
        return haystack.includes(searchQuery);
      });
    }
    return result;
  }, [allProducts, dbProducts, filters, searchQuery]);

  const bestSellers = allProducts.filter((p) => p.is_bestseller || p.badge === "mais vendido");
  const newArrivals = allProducts.filter((p) => p.badge === "novo");

  return (
    <div className="flex min-h-screen flex-col">
      <Helmet>
        <title>Loja do Instituto Eu Salvo Vidas | Cada compra salva uma vida</title>
        <meta name="description" content="Camisetas, canecas, ecobags e mais. 100% da renda apoia o resgate, cuidado e reabilitação de animais pelo Instituto Eu Salvo Vidas." />
        <link rel="canonical" href={`${window.location.origin}/`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Instituto Eu Salvo Vidas",
            url: window.location.origin,
            potentialAction: {
              "@type": "SearchAction",
              target: `${window.location.origin}/?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Instituto Eu Salvo Vidas",
            url: window.location.origin,
            description:
              "Organização dedicada ao resgate, cuidado e reabilitação de animais. A loja financia os resgates.",
          })}
        </script>
      </Helmet>
      <TopBar />
      <StoreHeader />
      <HeroBanner />
      <Suspense fallback={null}><CategoryHighlights /></Suspense>

      <section id="produtos" className="container py-10 lg:py-14">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h2 className="font-display text-2xl font-bold text-foreground lg:text-3xl">
            {searchQuery
              ? `Resultados para "${searchQuery}" (${filteredProducts.length})`
              : filters.category || "Todos os Produtos"}
          </h2>
          <Suspense fallback={null}>
            <StoreFilterSidebar products={allProducts} filters={filters} onChange={setFilters} drawerOnly />
          </Suspense>
        </div>
        {filteredProducts.length === 0 && searchQuery ? (
          <div className="py-12 text-center text-muted-foreground">
            Nenhum produto encontrado para "{searchQuery}".
          </div>
        ) : (
          <ProductGrid products={filteredProducts} />
        )}
      </section>

      {bestSellers.length > 0 && (
        <section className="bg-secondary/30 py-12 lg:py-16">
          <div className="container">
            <ProductGrid products={bestSellers} title="🔥 Mais Vendidos" />
          </div>
        </section>
      )}

      <Suspense fallback={null}><InstitutionalBanner /></Suspense>

      {newArrivals.length > 0 && (
        <section className="container py-12 lg:py-16">
          <ProductGrid products={newArrivals} title="✨ Novidades" />
        </section>
      )}

      <Suspense fallback={null}>
        <SocialProof />
        <Newsletter />
        <StoreFooter />
      </Suspense>
    </div>
  );
};

export default Index;
